// src/pages/DashboardPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Stack,
  Grid,
  Chip,
  LinearProgress,
  Popover,
  Divider
} from '@mui/material'
import PieChartIcon from '@mui/icons-material/PieChart'

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'


// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function DashboardPage() {
  // --------------------------------------------------------------------------
  // AUTH
  // --------------------------------------------------------------------------
  const { currentUser, organization } = useAuth()
  const { t } = useTranslation() // i18n hook

  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const routeFormId = useMemo(
    () => location.state?.formId || searchParams.get('formId'),
    [location.state, searchParams]
  )

  const [selectedForm, setSelectedForm] = useState(null)
  const [contextLoading, setContextLoading] = useState(true)
  const [contextError, setContextError] = useState('')

  const [responses, setResponses] = useState([])

  const [dateRange, setDateRange] = useState(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - 30)
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10)
    }
  })
  const [tempRange, setTempRange] = useState(dateRange)
  const [rangeAnchor, setRangeAnchor] = useState(null)

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })

  const handleOpenRange = (event) => {
    setTempRange(dateRange)
    setRangeAnchor(event.currentTarget)
  }
  const handleCloseRange = () => setRangeAnchor(null)
  const applyRange = () => {
    setDateRange(tempRange)
    setRangeAnchor(null)
  }
  const setPresetRange = (preset) => {
    const today = new Date()
    const toIso = (d) => d.toISOString().slice(0, 10)

    switch (preset) {
      case 'today':
        setTempRange({ start: toIso(today), end: toIso(today) })
        break
      case 'last7': {
        const start = new Date(today)
        start.setDate(start.getDate() - 6)
        setTempRange({ start: toIso(start), end: toIso(today) })
        break
      }
      case 'last30': {
        const start = new Date(today)
        start.setDate(start.getDate() - 29)
        setTempRange({ start: toIso(start), end: toIso(today) })
        break
      }
      case 'last365': {
        const start = new Date(today)
        start.setDate(start.getDate() - 364)
        setTempRange({ start: toIso(start), end: toIso(today) })
        break
      }
      case 'all':
      default:
        setTempRange({ start: '', end: '' })
        break
    }
  }

  const rangeLabel = useMemo(() => {
    if (!dateRange.start && !dateRange.end) return t('dashboard.rangeAllTime')
    if (dateRange.start && dateRange.end) return `${dateRange.start} - ${dateRange.end}`
    return dateRange.start || dateRange.end || t('dashboard.rangeAllTime')
  }, [dateRange, t])

  const parseDateInput = useCallback((value, isEnd = false) => {
    if (!value) return null
    const suffix = isEnd ? 'T23:59:59.999' : 'T00:00:00'
    const date = new Date(`${value}${suffix}`)
    return Number.isNaN(date.getTime()) ? null : date
  }, [])

  const getResponseDate = useCallback((resp) => {
    if (!resp) return null
    if (resp.completedAt?.toDate) return resp.completedAt.toDate()
    if (resp.completedAt instanceof Date) return resp.completedAt
    if (resp.createdAt?.toDate) return resp.createdAt.toDate()
    if (resp.createdAt instanceof Date) return resp.createdAt
    return null
  }, [])

  const getDurationMs = useCallback((resp) => {
    if (!resp) return null
    if (typeof resp.durationMs === 'number' && resp.durationMs >= 0) return resp.durationMs
    const normalizeDate = (date) =>
      date instanceof Date && !Number.isNaN(date.getTime()) ? date : null

    const completedAtDate = normalizeDate(
      resp.completedAt?.toDate
        ? resp.completedAt.toDate()
        : resp.completedAt instanceof Date
        ? resp.completedAt
        : null
    )
    const startedAtDate = normalizeDate(
      resp.startedAt?.toDate
        ? resp.startedAt.toDate()
        : resp.startedAt instanceof Date
        ? resp.startedAt
        : null
    )
    const createdAtDate = normalizeDate(
      resp.createdAt?.toDate
        ? resp.createdAt.toDate()
        : resp.createdAt instanceof Date
        ? resp.createdAt
        : null
    )
    const clientStartedAt = normalizeDate(
      resp.clientStartedAt ? new Date(resp.clientStartedAt) : null
    )

    if (completedAtDate && startedAtDate) return Math.max(completedAtDate - startedAtDate, 0)
    if (completedAtDate && clientStartedAt) return Math.max(completedAtDate - clientStartedAt, 0)
    if (completedAtDate && createdAtDate) return Math.max(completedAtDate - createdAtDate, 0)
    return null
  }, [])

  const formatDuration = useCallback((ms) => {
    if (ms === null || ms === undefined) return '-'
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return `${hours} sa ${remainingMinutes} dk`
    }
    return `${minutes} dk ${seconds} sn`
  }, [])

  const getDeviceLabel = useCallback(
    (deviceType) => {
      switch (deviceType) {
        case 'mobile':
          return t('dashboard.deviceMobile')
        case 'tablet':
          return t('dashboard.deviceTablet')
        case 'desktop':
          return t('dashboard.deviceDesktop')
        default:
          return t('dashboard.deviceUnknown')
      }
    },
    [t]
  )

  // --------------------------------------------------------------------------
  // 1) Workspace List (live onSnapshot)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const loadContext = async () => {
      if (!routeFormId) {
        setContextError(t('dashboard.formIdMissing'))
        setContextLoading(false)
        return
      }

      try {
        setContextLoading(true)
        setContextError('')

        const formSnap = await getDoc(doc(db, 'questionForms', routeFormId))
        if (!formSnap.exists()) {
          setContextError(t('dashboard.formNotFound'))
          setContextLoading(false)
          return
        }

        const formData = { id: formSnap.id, ...formSnap.data() }

        if (
          organization?.id &&
          formData.organizationId &&
          formData.organizationId !== organization.id
        ) {
          setContextError(t('dashboard.noAccess'))
          setContextLoading(false)
          return
        }

        if (
          organization?.id &&
          !formData.organizationId &&
          formData.createdBy &&
          currentUser?.uid !== formData.createdBy
        ) {
          setContextError(t('dashboard.noAccess'))
          setContextLoading(false)
          return
        }

        if (
          !formData.organizationId &&
          organization?.id &&
          formData.createdBy &&
          currentUser?.uid === formData.createdBy
        ) {
          await updateDoc(doc(db, 'questionForms', formData.id), {
            organizationId: organization.id
          })
          formData.organizationId = organization.id
        }

        setSelectedForm(formData)

      } catch (error) {
        console.error('Form bağlamı yüklenirken hata:', error)
        setContextError(t('dashboard.channelFormFetchError'))
      } finally {
        setContextLoading(false)
      }
    }

    loadContext()
  }, [routeFormId, currentUser, t, organization])

  // --------------------------------------------------------------------------
  // 5) Fetch selected form's answers
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedForm) {
      setResponses([]);
      return;
    }

    const fetchAnswers = async () => {
      try {
        const respRef = collection(db, 'responses')
        const qResp = query(respRef, where('questionFormId', '==', selectedForm.id))
        const respSnap = await getDocs(qResp)
        const rawResponses = respSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        setResponses(rawResponses)
      } catch (error) {
        console.error('Cevaplar alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('dashboard.answersLoadingError'),
          severity: 'error'
        })
      }
    }

    fetchAnswers()
  }, [selectedForm, t]);

  // --------------------------------------------------------------------------
  // Filters & derived response data
  // --------------------------------------------------------------------------
  const startDateObj = useMemo(
    () => parseDateInput(dateRange.start),
    [dateRange.start, parseDateInput]
  )
  const endDateObj = useMemo(
    () => parseDateInput(dateRange.end, true),
    [dateRange.end, parseDateInput]
  )

  const filteredResponses = useMemo(() => {
    return responses.filter((resp) => {
      const respDate = getResponseDate(resp)
      if (!respDate) return false
      if (startDateObj && respDate < startDateObj) return false
      if (endDateObj && respDate > endDateObj) return false
      return true
    })
  }, [responses, startDateObj, endDateObj, getResponseDate])

  const completionStats = useMemo(() => {
    const completedResponses = filteredResponses.filter((resp) => resp.status === 'completed')
    const uniqueCompletedUsers = new Set(
      completedResponses.map((resp) => resp.userId || resp.id)
    ).size
    const uniqueStartedUsers = new Set(
      filteredResponses.map((resp) => resp.userId || resp.id)
    ).size

    const memberCount = uniqueStartedUsers
    const completionRate =
      memberCount > 0 ? Math.min((uniqueCompletedUsers / memberCount) * 100, 100) : 0

    const durations = completedResponses
      .map((resp) => getDurationMs(resp))
      .filter((val) => typeof val === 'number' && val >= 0)

    const avgDurationMs =
      durations.length > 0
        ? Math.round(durations.reduce((sum, val) => sum + val, 0) / durations.length)
        : null

    const deviceCounts = {}
    completedResponses.forEach((resp) => {
      const key = resp.deviceInfo?.deviceType || 'unknown'
      deviceCounts[key] = (deviceCounts[key] || 0) + 1
    })

    const viaCounts = {}
    filteredResponses.forEach((resp) => {
      const key = resp.submittedVia || 'workspace'
      viaCounts[key] = (viaCounts[key] || 0) + 1
    })

    return {
      uniqueCompletedUsers,
      uniqueStartedUsers,
      completionRate,
      memberCount,
      avgDurationMs,
      deviceCounts,
      viaCounts,
      completedResponsesCount: completedResponses.length,
      totalResponses: filteredResponses.length
    }
  }, [filteredResponses, getDurationMs])

  const navigationQuery = useMemo(() => {
    const params = new URLSearchParams()
    if (selectedForm?.id) params.set('formId', selectedForm.id)
    if (selectedForm?.channelId) params.set('channelId', selectedForm.channelId)
    if (selectedForm?.workspaceId) params.set('workspaceId', selectedForm.workspaceId)
    const qs = params.toString()
    return qs ? `?${qs}` : ''
  }, [selectedForm])

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        background: 'linear-gradient(180deg, #f7f9fc 0%, #eef3fb 100%)'
      }}
    >
      <Box
        sx={{
          width: '100%',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            background:
              'linear-gradient(135deg, rgba(63, 81, 181, 0.06) 0%, rgba(33, 150, 243, 0.06) 100%)',
            border: '1px solid rgba(63, 81, 181, 0.08)',
            boxShadow: '0 10px 30px rgba(63, 81, 181, 0.12)'
          }}
        >

          {/* HEADER ROW: title solda, tab bar sağda */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
            spacing={2}
          >
            
            {/* SOL BLOK: Title, subtitle + published */}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PieChartIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {t('dashboard.pageTitle')}
                </Typography>
              </Stack>

              {/* Subtitle + published aynı satırda */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {selectedForm?.name || t('cardReporting.pageTitle')}
                </Typography>

                {selectedForm?.status && (
                  <Chip
                    label={selectedForm.status}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      borderRadius: 1.5,
                      fontWeight: 600,
                      px: 0.6
                    }}
                  />
                )}
              </Stack>
            </Stack>

            {/* TAB BAR */}
            <Stack
              direction={{ xs: 'row', sm: 'row' }}
              spacing={1}
              sx={{
                p: 0.6,
                borderRadius: 999,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                maxWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              <Button
                component={Link}
                to={`/view-answers${navigationQuery}`}
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  boxShadow: 'none'
                }}
              >
                {t('formNavigation.dashboard')}
              </Button>

              <Button
                component={Link}
                to={`/view-answers-card${navigationQuery}`}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                {t('formNavigation.reporting')}
              </Button>

              <Button
                component={Link}
                to={`/CardAnalyticsPage${navigationQuery}`}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                {t('formNavigation.analytics')}
              </Button>
            </Stack>

          </Stack>
        </Paper>

        {contextLoading && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'center',
              borderRadius: 3,
              backgroundColor: '#fff'
            }}
          >
            <CircularProgress />
          </Paper>
        )}

        {!contextLoading && contextError && (
          <Alert severity="error" sx={{ width: '100%', borderRadius: 3 }}>
            {contextError}
          </Alert>
        )}

        {!contextLoading && !contextError && !selectedForm && (
          <Alert severity="warning" sx={{ width: '100%', borderRadius: 3 }}>
            {t('dashboard.formListError')}
          </Alert>
        )}

        {!contextLoading && !contextError && selectedForm && (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2.5, sm: 3 },
                backgroundColor: '#fff',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                border: '1px solid #edf1f7'
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('dashboard.channelFormFiltersTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('dashboard.filterHint')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleOpenRange}
                    endIcon={<span role="img" aria-label="calendar">📅</span>}
                    sx={{
                      justifyContent: 'space-between',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      height: 48
                    }}
                  >
                    {rangeLabel}
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Popover
              open={Boolean(rangeAnchor)}
              anchorEl={rangeAnchor}
              onClose={handleCloseRange}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
            >
              <Box sx={{ display: 'flex', width: 520, p: 0 }}>
                {/* Left column: Preset list */}
                <Box sx={{ width: 160, p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                  <Stack spacing={1}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setPresetRange('all')}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 500 }}
                    >
                      {t('dashboard.rangeAllTime')}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setPresetRange('today')}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 500 }}
                    >
                      {t('dashboard.rangeToday')}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setPresetRange('last7')}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 500 }}
                    >
                      {t('dashboard.rangeLast7')}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setPresetRange('last30')}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 500 }}
                    >
                      {t('dashboard.rangeLast30')}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setPresetRange('last365')}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 500 }}
                    >
                      {t('dashboard.rangeLast365')}
                    </Button>
                  </Stack>
                </Box>
                {/* Right column: Manual input and actions */}
                <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('dashboard.rangeTitle')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label={t('dashboard.startDate')}
                        value={tempRange.start}
                        onChange={(e) =>
                          setTempRange((prev) => ({ ...prev, start: e.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label={t('dashboard.endDate')}
                        value={tempRange.end}
                        onChange={(e) => setTempRange((prev) => ({ ...prev, end: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {t('dashboard.filterHint')}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 'auto' }}>
                    <Button onClick={handleCloseRange}>{t('dashboard.rangeCancel')}</Button>
                    <Button variant="contained" onClick={applyRange}>
                      {t('dashboard.rangeApply')}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Popover>

            <Paper
              variant="outlined"
              sx={{
                pt: { xs: 3, sm: 3, md: 3 },   // üst padding
                pb: { xs: 6, sm: 6, md: 6 },   // alt padding (AYRI)
                px: { xs: 3, sm: 3, md: 3 },   // sağ-sol padding
                backgroundColor: '#fff',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                border: '1px solid #edf1f7'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {t('dashboard.userStatsTitle')}
                </Typography>
                <Chip
                  variant="outlined"
                  color="primary"
                  label={t('dashboard.dateRangeApplied', {
                    start: dateRange.start || '-',
                    end: dateRange.end || '-'
                  })}
                />
              </Stack>
              <Grid container rowSpacing={3} columnSpacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#e3f2fd',
                      height: '80%'
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {t('dashboard.totalCompleted')}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                      {completionStats.uniqueCompletedUsers}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#d3cdf0ff',
                      height: '80%'
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {t('dashboard.startedCount')}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                      {completionStats.uniqueStartedUsers}
                    </Typography>

                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#e8f5e9',
                      height: '80%'
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {t('dashboard.completionRateLabel')}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1b5e20' }}>
                      {completionStats.completionRate.toFixed(1)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(completionStats.completionRate, 100)}
                      sx={{ mt: 1, height: 8, borderRadius: 5 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#fff8e1',
                      height: '80%'
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {t('dashboard.avgCompletionTime')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                      {completionStats.avgDurationMs !== null
                        ? formatDuration(completionStats.avgDurationMs)
                        : t('dashboard.noDurationData')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('dashboard.completedResponsesCount', {
                        count: completionStats.completedResponsesCount
                      })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#f3e5f5',
                      height: '80%'
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {t('dashboard.deviceBreakdown')}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                      {Object.keys(completionStats.deviceCounts || {}).length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          {t('dashboard.noDeviceData')}
                        </Typography>
                      )}
                      {Object.entries(completionStats.deviceCounts || {}).map(([device, count]) => (
                        <Chip
                          key={device}
                          size="small"
                          label={`${getDeviceLabel(device)}: ${count}`}
                          color="secondary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  )
}
